// src/pages/MypageRecords.tsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from '../api/axios';
import dayjs from 'dayjs';
import PaymentConfirmDialog from '../components/PaymentConfirmDialog';
import DiagnosisDetailDialog from '../components/DiagnosisDetailDialog';
import QuestionnaireDetailDialog from '../components/QuestionnaireDetailDialog';
import classNames from 'classnames';
import { CheckCircle2, Loader2, ExternalLink, Info } from 'lucide-react';
import { getUserProfile } from '../api/userProfile';
import UnifiedConfirmDialog from '../components/UnifiedConfirmDialog';

import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { useMypageContext } from '../components/MypageShell';

import ProfileGuardDialog, {
  GuardDecision,
  isProfileGuardSnoozed,
  markSnooze1Day,
  markSnoozeSession,
  SESSION_SNOOZE_KEY,
} from '../components/ProfileGuardDialog';

import WeChatGroupFloat from '../components/WeChatGroupFloat';
import { useAuth } from '../context/AuthContext';
import { translateMedicineName } from '../utils/medTranslations';

const OPT_KEY = 'mypage_profile_nudge_optout';
const SNOOZE_KEY = 'mypage_profile_nudge_snooze_until_ms';

interface UserProfile {
  full_name?: string;
  furigana?: string;
  gender?: string;
  birth_date?: string;
  phone_number?: string;
  postal_code?: string;
  address?: string;
  chart_number?: string | null;
}

interface MyReservation {
  id: number;
  service: string;
  date: string;
  time: string;
  status: string;
  visit_type?: 'first' | 'followup' | '' | string;
  zoom_join_url?: string;
  zoom_meeting_id?: string | null;
  prescription_url?: string;
  total_price?: number;
  total_amount_jpy?: number | null;
  prescription_text?: string | null;
  diagnosis?: string | null;
  notes?: string | null;
  user_profile?: UserProfile;
  is_questionnaire_filled?: boolean;
  cancel_reason?: string | null;
  payment_status?: 'paid' | 'unpaid' | 'failed' | null;
  receipt_url?: string | null;
  transaction_id?: string | null;

  shipping_status?: string | null;
  tracking_number?: string | null;
  shipped_at?: string | null;
  shipping_carrier?: string | null;

  patient_chart_number?: string | null;
  chart_number?: string | null;
  doctor_name?: string | null;

  // Refill fields
  refill_max_months?: number;
  refill_used_months?: number;
  refill_max_quantity?: number;
  refill_used_quantity?: number;
  is_refill?: boolean;
  is_refillable?: boolean;
}

const JOIN_EARLY_MINUTES = 30;
const JOIN_EXPIRES_MINUTES = 60;
const BTN_SIZE = '3.5rem';

interface RefillModalProps {
  reservation: MyReservation;
  onClose: () => void;
}

const RefillModal: React.FC<RefillModalProps> = ({ reservation, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [medicines, setMedicines] = useState<Array<{
    plan_id: number;
    name: string;
    plan_type: string;
    remaining_quantity: number;
    remaining_months: number;
    max_quantity: number;
    max_months: number;
    unit: string;
  }>>([]);

  const [selections, setSelections] = useState<Record<number, { selected: boolean; quantity: number }>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState('');
  // 追加配送は「予約作成 → 通常決済ダイアログ(クーポン適用可)」の順で進める
  const [paymentDlgOpen, setPaymentDlgOpen] = useState(false);
  const [paymentReservation, setPaymentReservation] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);                                    // /refills/request 送信中(連打防止)
  const [createdReservationId, setCreatedReservationId] = useState<number | null>(null);  // 作成済み追加配送予約(重複作成防止)

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/refills/status/${reservation.id}`);
        const data = res.data;

        if (data.medicines && data.medicines.length > 0) {
          setMedicines(data.medicines);
          const initialSelections: Record<number, { selected: boolean; quantity: number }> = {};
          data.medicines.forEach((med: any) => {
            const remaining = (med.unit === 'ヶ月' || med.unit === '月') ? med.remaining_months : med.remaining_quantity;
            initialSelections[med.plan_id] = { selected: remaining > 0, quantity: 1 };
          });
          setSelections(initialSelections);
        } else {
          const isMonth = (data.max_months > 0) && (data.max_quantity === 0);
          const unit = isMonth ? '月' : '個';
          setMedicines([{
            plan_id: 0,
            name: data.medicine_names || t('mypage.prescribed_medicine', '処方薬'),
            plan_type: isMonth ? 'subscription' : 'single',
            remaining_quantity: data.remaining_quantity,
            remaining_months: data.remaining_months,
            max_quantity: data.max_quantity,
            max_months: data.max_months,
            unit: unit
          }]);
          setSelections({ 0: { selected: true, quantity: 1 } });
        }
      } catch (e) {
        console.error(e);
        alert(t('mypage.load_failed'));
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [reservation.id, t, onClose]);

  const executeSubmit = async (selectedItems: typeof medicines) => {
    // 二重作成防止①: 既に予約を作成済みなら新規作成せず、同じ予約の支払いダイアログを再表示するだけ。
    // (支払いをキャンセル→再度「申請する」を押しても、未決済の追加配送予約が重複作成されないようにする)
    if (createdReservationId) {
      setPaymentReservation({ id: createdReservationId, service: (reservation as any).service });
      setPaymentDlgOpen(true);
      return;
    }
    // 二重作成防止②: 送信中の再入を無視(申請連打対策)
    if (submitting) return;
    setSubmitting(true);
    try {
      const items = selectedItems.map(med => ({
        plan_id: med.plan_id,
        quantity: selections[med.plan_id].quantity
      }));
      // 追加配送はまず未決済の予約を作成し(/refills/request)、通常決済と同じ支払いダイアログ
      // (クーポン適用・支払い方法ガイド付き)へ誘導する。直接 Stripe へは飛ばさない。
      const res = await axios.post<{ success: boolean; reservation_id: number }>('/refills/request', {
        parent_reservation_id: reservation.id,
        items: items
      });
      const newId = res.data?.reservation_id;
      if (newId) {
        // 作成した未決済の追加配送予約を、通常の支払いダイアログ(クーポン適用可)で決済させる
        setCreatedReservationId(newId);
        setPaymentReservation({ id: newId, service: (reservation as any).service });
        setPaymentDlgOpen(true);
      } else {
        // reservation_id が無い場合（想定外）は申請一覧へ遷移して手動決済を促す
        onClose();
        navigate('/mypage/extra?activeTab=applications');
      }
    } catch (e) {
      alert(t('mypage.refill_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    const selectedItems = medicines.filter(med => selections[med.plan_id]?.selected);
    if (selectedItems.length === 0) {
      alert(t('mypage.select_medicine_to_add'));
      return;
    }
    const summary = selectedItems.map(med => {
      const translatedName = translateMedicineName(med.name, i18n.language);
      return `${translatedName} × ${selections[med.plan_id].quantity}${med.unit}`;
    }).join('\n');
    setConfirmSummary(summary);
    setConfirmOpen(true);
  };

  const getRemaining = (med: any) => (med.unit === 'ヶ月' || med.unit === '月') ? med.remaining_months : med.remaining_quantity;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 shadow-xl">
          <Loader2 className="animate-spin w-8 h-8 text-coral mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-800 mb-4">{t('mypage.apply_extra_delivery')}</h3>
        <div className="space-y-3 mb-6">
          {medicines.map(med => {
            const remaining = getRemaining(med);
            const selection = selections[med.plan_id] || { selected: false, quantity: 1 };
            return (
              <div key={med.plan_id} className={`border rounded-lg p-3 ${remaining <= 0 ? 'bg-gray-50' : ''}`}>
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={selection.selected}
                    disabled={remaining <= 0}
                    onChange={e => setSelections(prev => ({ ...prev, [med.plan_id]: { ...prev[med.plan_id], selected: e.target.checked } }))}
                    className="w-5 h-5 mt-1 rounded text-coral disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{translateMedicineName(med.name, i18n.language)}</div>
                    <div className="text-xs text-gray-500 mt-1">{t('mypage.refill_remaining')}: {remaining} {med.unit}</div>
                    {selection.selected && remaining > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <label className="text-xs text-gray-600">数量:</label>
                        <input
                          type="number"
                          min="1"
                          max={remaining}
                          value={selection.quantity}
                          onChange={e => {
                            const val = Math.min(remaining, Math.max(1, Number(e.target.value)));
                            setSelections(prev => ({ ...prev, [med.plan_id]: { ...prev[med.plan_id], quantity: val } }));
                          }}
                          className="w-20 border rounded px-2 py-1 text-sm"
                        />
                        <span className="text-xs text-gray-600">{med.unit}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-3 border rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition">{t('common.cancel')}</button>
          <button
            onClick={handleConfirm}
            disabled={!Object.values(selections).some(s => s.selected) || submitting}
            className={`flex-1 px-4 py-3 rounded-xl font-bold transition ${Object.values(selections).some(s => s.selected) && !submitting ? 'bg-coral text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
          >
            {t('mypage.confirm_apply', '申請する')}
          </button>
        </div>
      </div>

      <UnifiedConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('common.confirm') || '確認'}
        message={t('mypage.apply_confirm_body', { summary: confirmSummary, defaultValue: `追加配送を申請しますか？\n\n${confirmSummary}` })}
        onConfirm={() => {
          // 確認後、まず追加配送予約を作成し、通常決済と同じ支払いダイアログへ誘導する
          setConfirmOpen(false);
          const selectedItems = medicines.filter(med => selections[med.plan_id]?.selected);
          executeSubmit(selectedItems);
        }}
      />

    </div>

      {/* 追加配送予約を通常決済と同じダイアログ(クーポン適用・支払い方法ガイド付き)で決済する。
          ※ onClose 遮罩(上の div)の外に配置すること: ダイアログ内のクリックが遮罩へ伝播して
             RefillModal ごと閉じてしまう不具合(クーポン操作不可)を防ぐ(F1 修正)。 */}
      <PaymentConfirmDialog
        open={paymentDlgOpen}
        reservation={paymentReservation}
        onClose={() => setPaymentDlgOpen(false)}
        onConfirm={async () => {
          // PaymentConfirmDialog が Stripe URL へリダイレクト済み。ここでは閉じるのみ。
          setPaymentDlgOpen(false);
        }}
      />
    </>
  );
};

// 医師キャンセル理由を notes から抽出
const extractCancelReasonFromNotes = (notes?: string | null) => {
  if (!notes) return '';
  const firstLine = String(notes).split('\n')[0];
  const m = firstLine.match(/\[Doctor\s*Cancelled\]\s*(.+)$/i);
  return m ? m[1].trim() : '';
};

const digits = (s?: string | null) => String(s || '').replace(/\D/g, '');
const isYYYYMMDD = (s?: string | null) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

const MypageRecords: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const [refillTarget, setRefillTarget] = useState<MyReservation | null>(null); // State for Modal

  type AuthMaybe = {
    user?: { chart_number?: string | null } | null;
    currentUser?: { chart_number?: string | null } | null;
    me?: { chart_number?: string | null } | null;
    chart_number?: string | null;
  };
  const auth = useAuth() as unknown as AuthMaybe;


  const [reservations, setReservations] = useState<MyReservation[]>([]);
  const [cancelingId, setCancelingId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'waiting' | 'done'>('waiting');
  const [showPayment, setShowPayment] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<MyReservation | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<MyReservation | null>(null);

  const [showDetail, setShowDetail] = useState<MyReservation | null>(null);

  // 決済バナーの状態（同期中・完了など）
  const [payBanner, setPayBanner] = useState<{ rid?: number; phase: 'hidden' | 'success' | 'syncing' | 'done' }>({
    phase: 'hidden',
  });

  const [confirmConfig, setConfirmConfig] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'question' | 'danger' | 'warning';
  }>({ open: false, title: '', message: '', onConfirm: () => { } });

  const { profileStatus } = useMypageContext();
  const [showPwdDlg, setShowPwdDlg] = useState(false);

  const [guardOpen, setGuardOpen] = useState(false);
  const guardResolverRef = useRef<((d: GuardDecision) => void) | null>(null);

  // 予約カード DOM 参照（スクロール用）
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const setCardRef = (id: number) => (el: HTMLDivElement | null) => {
    const map = cardRefs.current;
    if (el) map.set(id, el);
    else map.delete(id);
  };

  const [scrollTargetRid, setScrollTargetRid] = useState<number | null>(null);

  // 指定 ID のカードへスクロール＋強調表示
  const scrollToRid = (rid: number, highlight = true) => {
    const start = Date.now();
    const tryOnce = () => {
      const el = cardRefs.current.get(rid) || document.getElementById(`reservation-card-${rid}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (highlight) {
          el.classList.add('ring-2', 'ring-orange-400', 'ring-offset-2');
          setTimeout(() => el.classList.remove('ring-2', 'ring-orange-400', 'ring-offset-2'), 1600);
        }
        return true;
      }
      return false;
    };

    if (tryOnce()) return;
    const timer = setInterval(() => {
      if (tryOnce() || Date.now() - start > 4000) clearInterval(timer);
    }, 150);
  };

  // カルテ番号の取得（Auth / プロフィール / localStorage から順に探す）
  const pickChartNumber = (): string | null => {
    const fromAuth =
      auth?.user?.chart_number ??
      auth?.currentUser?.chart_number ??
      auth?.me?.chart_number ??
      auth?.chart_number ??
      null;
    if (fromAuth) return fromAuth;

    const fromProfile = userProfile?.chart_number ?? null;
    if (fromProfile) return fromProfile;

    try {
      const keys = ['current_user', 'user', 'me'];
      for (const k of keys) {
        const raw = localStorage.getItem(k);
        if (raw) {
          const u = JSON.parse(raw);
          const cn =
            u?.chart_number ??
            u?.profile?.chart_number ??
            null;
          if (cn) return cn;
        }
      }
    } catch { }
    return null;
  };

  // プロフィール情報取得（userProfile は pickChartNumber で使用）
  const fetchProfile = async () => {
    try {
      const p = await getUserProfile();
      setUserProfile(p || null);
    } catch {
      setUserProfile(null);
    }
  };

  // 予約一覧取得
  const fetchReservations = async () => {
    const res = await axios.get<MyReservation[]>('/reservations');
    const mapped = (res.data || []).map((r) => {
      const next: MyReservation = { ...r };
      if (!next.cancel_reason && next.status === 'doctor_cancelled') {
        const parsed = extractCancelReasonFromNotes(next.notes);
        if (parsed) next.cancel_reason = parsed;
      }
      return next;
    });
    setReservations(mapped);
  };

  // 最新の Zoom 参加 URL を取得（URL が更新されている場合に備える）
  const fetchLatestJoinUrl = async (id: number): Promise<string | null> => {
    try {
      const res = await axios.get<MyReservation>(`/reservations/${id}`);
      return res.data?.zoom_join_url || null;
    } catch {
      return null;
    }
  };

  // 初期ロード：予約＋プロフィール
  useEffect(() => {
    fetchReservations().catch((e) => console.error('Fetch reservations failed', e));
    fetchProfile().catch(() => { });
  }, []);

  // URL パラメータ ?status=completed 等で「診療履歴」タブに飛ぶ
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = (params.get('status') || '').toLowerCase();
    if (status === 'completed' || status === 'done') {
      setActiveTab('done');
    }
  }, [location.search]);

  // ?open_payment=true&rid=xxx → 支払いダイアログを自動オープン（マジックリンク経由）
  useEffect(() => {
    if (reservations.length === 0) return;
    const params = new URLSearchParams(location.search);
    if (params.get('open_payment') !== 'true') return;
    const rid = params.get('rid');
    if (!rid) return;
    const target = reservations.find(r => String(r.id) === rid);
    if (target) {
      setActiveTab('done');
      setSelectedReservation(target);
      setShowPayment(true);
      // URL パラメータを消してダイアログの二重起動を防ぐ
      navigate(location.pathname, { replace: true });
    }
  }, [reservations, location.search, navigate]);

  // 予約キャンセル処理
  const handleCancel = async (id: number) => {
    setConfirmConfig({
      open: true,
      title: t('common.confirm') || '確認',
      message: t('mypage.cancel_confirm'),
      type: 'danger',
      onConfirm: async () => {
        setConfirmConfig(prev => ({ ...prev, open: false }));
        try {
          setCancelingId(id);
          await axios.delete(`/reservations/${id}`);
          await fetchReservations();
          alert(t('mypage.cancel_success'));
        } catch (err) {
          console.error('cancel failed', err);
          alert(t('mypage.cancel_failed'));
        } finally {
          setCancelingId(null);
        }
      }
    });
  };

  // 決済後リダイレクト (?pay=success/cancel) の処理と決済結果同期
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pay = params.get('pay');
    const ridStr = params.get('rid');
    const sessionId = params.get('session_id') || '';
    const rid = ridStr ? Number(ridStr) : undefined;

    // URL に pay が無い場合は、バナーを必ず閉じる
    if (!pay) {
      if (payBanner.phase !== 'hidden') {
        setPayBanner({ phase: 'hidden' });
      }
      return;
    }

    // 決済パラメータがある場合は診療履歴タブに切り替え
    setActiveTab('done');
    if (rid) setScrollTargetRid(rid);

    // URL クエリを少し後で消しておく（F5 で再同期しないように）
    const clearQuerySoon = () =>
      setTimeout(() => navigate(location.pathname, { replace: true }), 300);

    // キャンセル戻り
    if (pay === 'cancel') {
      setPayBanner({ phase: 'hidden' });
      if (rid) scrollToRid(rid);
      clearQuerySoon();
      return;
    }

    // 成功戻り
    if (pay === 'success' && rid) {
      let stopped = false;

      (async () => {
        try {
          // まず session_id から確定を試す
          setPayBanner({ rid, phase: 'syncing' });
          if (sessionId) {
            const resp = await axios.post('/pay/confirm', { session_id: sessionId });
            if (resp.data?.payment_status === 'paid') {
              await fetchReservations();
              if (!stopped) {
                setPayBanner({ rid, phase: 'done' });
                setTimeout(() => setPayBanner({ phase: 'hidden' }), 3000);
              }
              scrollToRid(rid);
              clearQuerySoon();
              return;
            }
          }
        } catch {
          // 失敗しても後続のポーリングに進む
        }

        const start = Date.now();

        const poll = async () => {
          if (stopped) return;

          try {
            // /pay/status で決済状態をポーリング
            const s = await axios.get('/pay/status', { params: { reservation_id: rid } });
            if (s.data?.payment_status === 'paid') {
              await fetchReservations();
              setPayBanner({ rid, phase: 'done' });
              setTimeout(() => setPayBanner({ phase: 'hidden' }), 3000);
              scrollToRid(rid);
              clearQuerySoon();
              return;
            }
          } catch {
            // /pay/status が落ちている場合は /reservations から直接チェック
            try {
              const res = await axios.get<MyReservation[]>('/reservations');
              const list = res.data || [];
              setReservations(list);
              const found = list.find((x) => x.id === rid);
              if (found?.payment_status === 'paid') {
                setPayBanner({ rid, phase: 'done' });
                setTimeout(() => setPayBanner({ phase: 'hidden' }), 3000);
                scrollToRid(rid);
                clearQuerySoon();
                return;
              }
            } catch {
              // ここは何もせずタイムアウト判定へ
            }
          }

          // 最大 15 秒待っても確定しない場合は、同期完了扱いにして強制終了
          if (Date.now() - start > 15000) {
            await fetchReservations();
            setPayBanner({ rid, phase: 'done' });
            setTimeout(() => setPayBanner({ phase: 'hidden' }), 3000);
            scrollToRid(rid);
            clearQuerySoon();
            return;
          }

          setTimeout(poll, 1500);
        };

        poll();
      })();

      return () => {
        // コンポーネント unmount 時などにポーリングを停止
        stopped = true;
      };
    }
  }, [location.search, navigate, payBanner.phase]);

  // 診療履歴タブ表示時の初期スクロール（rid or 最初の未払い）
  useEffect(() => {
    if (activeTab !== 'done') return;

    const params = new URLSearchParams(location.search);
    const ridFromUrl = params.get('rid');
    const rid = scrollTargetRid || (ridFromUrl ? Number(ridFromUrl) : null);

    if (rid) {
      scrollToRid(rid, true);
      return;
    }

    const firstUnpaid = reservations.find((r) => r.status === 'completed' && r.payment_status !== 'paid');
    if (firstUnpaid) {
      setScrollTargetRid(firstUnpaid.id);
      scrollToRid(firstUnpaid.id, true);
    }
  }, [activeTab, reservations, location.search, scrollTargetRid]);

  // タブ別フィルタリング
  const filtered = useMemo(
    () =>
      reservations.filter((r) =>
        r.visit_type !== 'refill' && !r.is_refill &&
        (activeTab === 'waiting'
          ? r.status === 'pending' || r.status === 'reserved' || r.status === 'assigned' || r.status === 'expired'
          : ['completed', 'canceled', 'doctor_cancelled', 'admin_cancelled'].includes(r.status))
      ),
    [reservations, activeTab]
  );

  const fmtJPY = (n?: number | null) =>
    typeof n === 'number' && Number.isFinite(n) ? new Intl.NumberFormat('ja-JP').format(n) : '-';

  // ステータス表示（色・文言）
  const renderStatus = (r: MyReservation) => {
    if (r.status === 'doctor_cancelled') {
      const reason = r.cancel_reason || extractCancelReasonFromNotes(r.notes);
      return (
        <span className="inline-flex items-center gap-2 min-w-0">
          <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-red-50 text-red-600 border border-red-100 whitespace-nowrap">
            {t('mypage.doctor_cancelled')}
          </span>
          {reason ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-red-600 bg-gray-100 border border-red-200 max-w-[240px] truncate"
              title={reason}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5" />
              {reason}
            </span>
          ) : null}
        </span>
      );
    } else if (r.status === 'admin_cancelled') {
      return (
        <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-red-50 text-red-600 border border-red-100 whitespace-nowrap">
          {t('status.admin_cancelled', '管理者キャンセル')}
        </span>
      );
    } else if (r.status === 'canceled') {
      return (
        <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-gray-50 text-gray-500 border border-gray-200 whitespace-nowrap">
          {t('mypage.user_cancelled')}
        </span>
      );
    } else if (r.status === 'completed') {
      if (r.payment_status === 'paid') {
        return (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
              <CheckCircle2 className="w-4 h-4" />
              {r.shipping_status === 'shipped' ? t('payment.medicine_shipped') : t('payment.paid')}
            </span>
            {typeof r.total_amount_jpy === 'number' && Number.isFinite(r.total_amount_jpy) && (
              <span className="text-gray-500 text-xs">（{fmtJPY(r.total_amount_jpy)} 円）</span>
            )}
            <button
              onClick={async () => {
                // 領収書は常に自社バックエンド(S3→Stripe領収書→自己生成)から配信する。
                // Stripeの公開ページ(請求書/領収書の両方が選べてしまう)へは飛ばさない=患者に必ず「領収書」を渡す。
                // ポップアップブロック回避: クリック直後(ユーザー操作中)に空タブを開き、取得後にそのタブへ遷移させる。
                const win = window.open('', '_blank');
                try {
                  const res = await axios.get(`/receipts/pdf/${r.id}`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  if (win) { win.location.href = url; } else { window.open(url, '_blank'); }
                } catch (e) {
                  if (win) win.close();
                  alert(t('payment.receipt_open_failed') || '領収書を開くことができませんでした');
                }
              }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-coral bg-coral/5 hover:bg-coral-light border border-coral/20 px-2 py-0.5 rounded-full cursor-pointer"
              title={t('payment.open_receipt') || '領収書を開く'}
            >
              {t('payment.receipt') || '領収書'}
              <ExternalLink className="w-3 h-3" />
            </button>
            {r.tracking_number ? (
              <a
                href={(() => {
                  // コピペ由来のハイフン/空白を除去（照会URLは素の番号前提）。carrier は yupack を正とし旧 yu_pack も許容
                  const n = (r.tracking_number || '').replace(/[^0-9A-Za-z]/g, '');
                  const c = r.shipping_carrier;
                  return c === 'sagawa'
                    ? `https://k2k.sagawa-exp.co.jp/p/web/okurijosearch.do?okurijoNo=${n}`
                    : (c === 'yupack' || c === 'yu_pack')
                      ? `https://trackings.post.japanpost.jp/services/srv/search/direct?searchKind=S004&locale=ja&reqCodeNo1=${n}`
                      : `https://toi.kuronekoyamato.co.jp/cgi-bin/tneko?number01=${n}`;
                })()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full"
                title={t('mypage.tracking_number') || '配送状況を確認'}
              >
                {t('common.tracking_number') || '追跡番号'}
                <ExternalLink className="w-3 h-3" />
              </a>
            ) : null}
          </span>
        );
      }
      return (
        <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 whitespace-nowrap">
          {t('mypage.completed')}
        </span>
      );
    } else {
      let colorClass = "bg-gray-50 text-gray-700 border-gray-200";
      if (['pending', 'reserved', 'assigned'].includes(r.status)) {
        colorClass = "bg-amber-50 text-amber-600 border-amber-100";
      } else if (r.status === 'expired' || r.status === 'failed') {
        colorClass = "bg-red-50 text-red-600 border-red-100";
      }

      return (
        <span className={`text-[11px] font-bold px-3 py-1 rounded-full border whitespace-nowrap ${colorClass}`}>
          {t(`status.${r.status}`, r.status)}
        </span>
      );
    }
  };

  const btnLongBase = `
    w-full flex items-center justify-center gap-2
    px-6 py-3 rounded-xl text-[14px] font-bold
    transition-all duration-200 shadow-sm active:scale-[0.98]
  `;
  const btnOrange = 'bg-orange-500 text-white hover:bg-orange-600 shadow-orange-100';
  const btnIndigo = 'border-2 border-orange-500 text-orange-500 bg-white hover:bg-orange-50 shadow-sm';
  const btnEmerald = 'border-2 border-orange-500 text-orange-500 bg-white hover:bg-orange-50 shadow-sm';
  const btnDisabled = 'bg-orange-500/50 text-white cursor-not-allowed shadow-none';
  const btnPurple = 'border-2 border-orange-500 text-orange-500 bg-white hover:bg-orange-50 shadow-sm';

  return (
    <>
      {payBanner.phase !== 'hidden' && (
        <div className="fixed top-16 left-0 right-0 z-40">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <div
              className={classNames(
                'rounded-xl border px-4 py-3 shadow-sm',
                payBanner.phase === 'done' ? 'bg-orange-50 border-orange-200' : 'bg-coral/5 border-coral/20'
              )}
            >
              <div className="flex items-center gap-2 text-sm">
                {payBanner.phase === 'syncing' ? (
                  <Loader2 className="w-4 h-4 animate-spin text-coral" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-orange-500" />
                )}
                <span className="text-gray-800">
                  {payBanner.phase === 'success' && t('payment.returned_success')}
                  {payBanner.phase === 'syncing' && t('payment.syncing')}
                  {payBanner.phase === 'done' && t('payment.synced_done')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col flex-1">
        {/* タブ切り替え */}
        <div className="flex gap-6 border-b mb-4">
              <div
                className={`px-1.5 py-2 text-sm md:text-base font-bold border-b-2 cursor-pointer ${activeTab === 'waiting' ? 'text-coral border-coral' : 'text-gray-400 border-transparent'}`}
                onClick={() => setActiveTab('waiting')}
              >
                {t('mypage.tab_waiting')}
              </div>
              <div
                className={`px-1.5 py-2 text-sm md:text-base font-bold border-b-2 cursor-pointer ${activeTab === 'done' ? 'text-coral border-coral' : 'text-gray-400 border-transparent'}`}
                onClick={() => setActiveTab('done')}
              >
                {t('mypage.tab_done')}
              </div>
            </div>

          {/* 予約カード一覧 */}
          <div className="space-y-6">
            {filtered.length > 0 ? (
              filtered.map((r) => {
                const reservationDateTime = dayjs(`${r.date} ${r.time}`, 'YYYY-MM-DD HH:mm');
                const now = dayjs();

                const canStart =
                  now.isAfter(reservationDateTime.subtract(JOIN_EARLY_MINUTES, 'minute')) &&
                  now.isBefore(reservationDateTime.add(JOIN_EXPIRES_MINUTES, 'minute'));

                const isPending = r.status === 'pending';
                const isAssigned = r.status === 'assigned';
                const expiredJoinWindow = now.isAfter(reservationDateTime.add(JOIN_EXPIRES_MINUTES, 'minute'));

                const questionnaireFilled = !!r.is_questionnaire_filled;
                const needsQuestionnaire =
                  (isPending || isAssigned || r.status === 'reserved' || r.status === 'expired') && !questionnaireFilled;

                const recordComplete = !!(r.diagnosis && r.prescription_text && r.notes);

                return (
                  <div
                    key={r.id}
                    id={`reservation-card-${r.id}`}
                    ref={setCardRef(r.id)}
                    className={classNames(
                      'relative overflow-hidden border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row justify-between sm:items-start gap-5 sm:gap-6 transition-shadow hover:shadow-md bg-white'
                    )}
                  >
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-amber-400" />
                    {/* 左側：予約情報 */}
                    <div className="flex-1 min-w-0">
                      <p className="text-lg font-bold text-gray-800 mb-1 leading-snug">
                        <span className="whitespace-normal">
                          {t(`service.${r.service}`, r.service)}
                        </span>
                        {r.visit_type && (
                          <span className="inline-block whitespace-nowrap text-sm text-gray-600">
                            （{t(`visit_type.${r.visit_type}`, r.visit_type)}）
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-600">
                        {r.date} {r.time}
                        {activeTab === 'waiting' && !isPending && expiredJoinWindow && (
                          <span className="ml-2 text-xs text-red-600">
                            {t('mypage.expired_join_window', { minutes: JOIN_EXPIRES_MINUTES })}
                          </span>
                        )}
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {activeTab === 'waiting' ? (
                          isPending ? (
                            <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-amber-50 text-amber-600 border border-amber-100 whitespace-nowrap">
                               {t('mypage.tab_pending') || '調整中（予約プール）'}
                            </span>
                          ) : r.status === 'assigned' ? (
                            <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 whitespace-nowrap">
                               {t('mypage.assigned_doctor') || '担当医確定'}
                            </span>
                          ) : (
                            <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-slate-50 text-slate-500 border border-slate-200 whitespace-nowrap">
                               {t('mypage.reserved')}
                            </span>
                          )
                        ) : (
                          renderStatus(r)
                        )}
                      </div>

                      {activeTab === 'waiting' && isPending && (
                        <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                          * {t('mypage.profile_warn_cancel') ||
                            '情報が未入力/不完全な場合、医師により予約がキャンセルされる可能性があります。'}
                        </p>
                      )}

                      {activeTab === 'waiting' && !isPending && (
                        <>
                          <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">{t('mypage.consultation_notice')}</p>
                          <p className="text-[11px] text-red-400 mt-1 font-bold leading-relaxed">{t('mypage.zoom_notice')}</p>
                        </>
                      )}
                    </div>

                    <div className="flex flex-col w-full sm:w-48 self-center sm:self-start gap-3">
                      {/* 「これからの診療」タブのアクション */}
                      {(activeTab === 'waiting') && (isPending || isAssigned || r.status === 'reserved' || r.status === 'expired') && (
                        <div className="flex flex-col w-full gap-3">
                          <div className="space-y-2 w-full">
                            {/* 問診票 */}
                            <div className="flex flex-col w-full relative">
                              <button
                                onClick={async () => {
                                  const ok = await ensureProfileGuard();
                                  if (!ok) return;

                                  if (needsQuestionnaire) {
                                    const path = `/questionnaire/${r.service.replace(/_/g, '-')}`;
                                    navigate(path, {
                                      state: {
                                        reservationId: r.id,
                                        fromMyPage: true,
                                        visitType: (r.visit_type as 'first' | 'followup') || 'first',
                                        service: r.service,
                                      },
                                    });
                                  } else {
                                    setSelectedQuestionnaire(r);
                                    setShowQuestionnaire(true);
                                  }
                                }}
                                className={classNames(btnLongBase, needsQuestionnaire ? btnOrange : btnEmerald)}
                              >
                                {needsQuestionnaire ? t('mypage.fill_questionnaire') : t('mypage.view_questionnaire')}
                              </button>

                              {needsQuestionnaire && (
                                <p className="text-[10px] text-red-500 font-bold mt-1 text-right pr-2 font-sans">
                                   * {t('mypage.need_input_badge', '事前入力が必要です！')}
                                </p>
                              )}
                            </div>

                             {r.zoom_meeting_id && (
                               <div className="text-[10px] text-gray-500 font-mono text-center mb-1">ID: {r.zoom_meeting_id}</div>
                             )}
                             {/* 診療開始ボタン */}
                             <button
                              onClick={async () => {
                                if (!canStart) return;
                                const latest = await fetchLatestJoinUrl(r.id);
                                const url = latest || r.zoom_join_url;
                                if (url) window.location.href = url;
                              }}
                              disabled={isPending || !canStart}
                              className={classNames(btnLongBase, (isPending || !canStart) ? btnDisabled : btnOrange)}
                              title={!canStart ? (t('mypage.start_unavailable_now') || '') : ''}
                            >
                               {t('mypage.start_consultation')}
                            </button>
                          </div>

                           <button
                             onClick={() => handleCancel(r.id)}
                             disabled={cancelingId === r.id}
                             className={classNames(
                               "text-[13px] underline decoration-slate-400 underline-offset-4 transition-colors text-center w-full mt-2 min-h-[44px] cursor-pointer flex items-center justify-center",
                               cancelingId === r.id
                                 ? "text-slate-200 cursor-not-allowed decoration-transparent"
                                 : "text-slate-500 hover:text-red-500 active:text-red-600"
                             )}
                           >
                             {cancelingId === r.id ? t('mypage.canceling') : t('mypage.cancel_reservation')}
                           </button>

                          {!isPending && (
                            <p className="mt-1 text-[10px] text-indigo-500 font-bold text-center animate-pulse">
                              {t('mypage.doctor_joins_within_15m')}
                            </p>
                          )}
                        </div>
                      )}

                      {/* 診療履歴タブ：未払い（支払い＋診療内容閲覧） */}
                      {activeTab === 'done' &&
                        r.status === 'completed' &&
                        recordComplete &&
                        r.payment_status !== 'paid' && (
                          <div className="flex flex-col gap-2 w-full">
                            {/* ⑦ 支払い予定金額をボタン前に表示 */}
                            {typeof r.total_amount_jpy === 'number' && r.total_amount_jpy > 0 && (
                              <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-center">
                                <p className="text-[11px] text-gray-500 mb-0.5">{t('mypage.payment_due_label') || 'お支払い予定金額'}</p>
                                <p className="text-lg font-bold text-orange-500">¥{r.total_amount_jpy.toLocaleString()}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">{t('mypage.coupon_apply_at_payment') || '※クーポンは支払い時に適用できます'}</p>
                              </div>
                            )}
                            <button
                              onClick={() => {
                                setSelectedReservation(r);
                                setShowPayment(true);
                              }}
                              className={classNames(btnLongBase, btnOrange)}
                            >
                              {t('mypage.pay_now')}
                            </button>

                            <button
                              onClick={() => {
                                const cn =
                                  pickChartNumber() ??
                                  (r as any)?.chart_number ??
                                  (r as any)?.patient_chart_number ??
                                  (r.user_profile as any)?.chart_number ??
                                  (userProfile as any)?.chart_number ??
                                  null;

                                setShowDetail({
                                  ...r,
                                  chart_number: cn ?? undefined,
                                  patient_chart_number: (r as any)?.patient_chart_number ?? cn ?? undefined,
                                  user_profile: {
                                    ...(r.user_profile || {}),
                                    chart_number: (r.user_profile as any)?.chart_number ?? cn ?? undefined,
                                  },
                                } as any);
                              }}
                              className={classNames(btnLongBase, btnIndigo)}
                            >
                              {t('mypage.view_diagnosis')}
                            </button>
                          </div>
                        )}

                      {/* 診療履歴タブ：支払い済み（次回予約など） */}
                      {activeTab === 'done' && r.status === 'completed' && r.payment_status === 'paid' && recordComplete && (
                        <div className="flex flex-col gap-2 w-full">
                          <button
                            onClick={() => navigate('/reserve')}
                            className={classNames(btnLongBase, btnOrange)}
                          >
                            {t('mypage.reserve_again', '診療予約')}
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (r.is_refillable) setRefillTarget(r);
                            }}
                            disabled={!r.is_refillable}
                            className={classNames(btnLongBase, r.is_refillable ? btnOrange : btnDisabled)}
                          >
                            {t('mypage.apply_extra_delivery', '追加配送')}
                          </button>

                          <button
                            onClick={() => {
                              const cn =
                                pickChartNumber() ??
                                (r as any)?.chart_number ??
                                (r as any)?.patient_chart_number ??
                                (r.user_profile as any)?.chart_number ??
                                (userProfile as any)?.chart_number ??
                                null;

                              setShowDetail({
                                ...r,
                                chart_number: cn ?? undefined,
                                patient_chart_number: (r as any)?.patient_chart_number ?? cn ?? undefined,
                                user_profile: {
                                  ...(r.user_profile || {}),
                                  chart_number: (r.user_profile as any)?.chart_number ?? cn ?? undefined,
                                },
                              } as any);
                            }}
                            className={classNames(btnLongBase, btnIndigo)}
                          >
                            {t('mypage.view_diagnosis')}
                          </button>
                        </div>
                      )}

                      {/* 診療完了だが診療記録が未入力の場合の案内 */}
                      {activeTab === 'done' && r.status === 'completed' && !recordComplete && (
                        <div className="w-full sm:w-auto">
                          <div
                            className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 max-w-md"
                            role="status"
                            aria-live="polite"
                          >
                            <Info className="w-4 h-4 mt-0.5 text-amber-600" />
                            <p className="text-sm text-amber-800">
                              {t('mypage.medical_record_pending')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-gray-500 text-sm mt-6">
                {activeTab === 'waiting' ? t('mypage.no_waiting') : t('mypage.no_done')}
              </p>
            )}
          </div>
      </div>

      {/* 診療内容詳細ダイアログ */}
      {showDetail && (
        <DiagnosisDetailDialog
          onClose={() => setShowDetail(null)}
          reservation={showDetail as any}
        />
      )}

      {/* 決済確認ダイアログ */}
      {showPayment && selectedReservation && (
        <PaymentConfirmDialog
          open={showPayment}
          onClose={() => {
            setShowPayment(false);
            setSelectedReservation(null);
          }}
          onConfirm={() => {
            setShowPayment(false);
          }}
          reservation={selectedReservation as any}
        />
      )}

      {/* 問診票閲覧ダイアログ */}
      {selectedQuestionnaire && showQuestionnaire && (
        <QuestionnaireDetailDialog
          key={`${selectedQuestionnaire.id}-${selectedQuestionnaire.service}-${selectedQuestionnaire.visit_type || ''}`}
          open={showQuestionnaire}
          onClose={() => {
            setShowQuestionnaire(false);
            setSelectedQuestionnaire(null);
          }}
          reservationId={selectedQuestionnaire.id}
          service={selectedQuestionnaire.service}
          {...({ visitType: selectedQuestionnaire.visit_type || '' } as any)}
        />
      )}

      {/* パスワード変更ダイアログ */}
      <ChangePasswordDialog
        open={showPwdDlg}
        onClose={() => setShowPwdDlg(false)}
        onSuccess={() => setShowPwdDlg(false)}
      />

      {/* プロフィール未入力ガードダイアログ */}
      <ProfileGuardDialog
        open={guardOpen}
        missingFields={profileStatus.missing}
        onClose={(d) => {
          setGuardOpen(false);
          const r = guardResolverRef.current;
          guardResolverRef.current = null;
          if (r) r(d);
        }}
      />

      {refillTarget && (
        <RefillModal
          reservation={refillTarget}
          onClose={() => setRefillTarget(null)}
        />
      )}
      <UnifiedConfirmDialog
        open={confirmConfig.open}
        onClose={() => setConfirmConfig(prev => ({ ...prev, open: false }))}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        type={confirmConfig.type as any}
      />
      <WeChatGroupFloat />
    </>
  );

  // プロフィール入力ガードを実行（戻り値 true のときだけ処理続行）
  async function ensureProfileGuard(): Promise<boolean> {
    const decision = await openProfileGuard();
    if (decision === 'skip') return true;
    if (decision === 'goProfile') {
      navigate('/mypage/profile');
      return false;
    }
    if (decision === 'snoozeSession') {
      markSnoozeSession();
      return true;
    }
    if (decision === 'snooze1d') {
      markSnooze1Day();
      return true;
    }
    if (decision === 'continue') return true;
    return false;
  }

  // プロフィールガードのダイアログを開くか判定
  function openProfileGuard(): Promise<GuardDecision | 'skip'> {
    if (profileStatus.state !== 'red') return Promise.resolve('skip');
    if (isProfileGuardSnoozed() || isSnoozedByLocal() || isOptOut()) return Promise.resolve('skip');

    return new Promise<GuardDecision>((resolve) => {
      guardResolverRef.current = resolve;
      setGuardOpen(true);
    });
  }

  // プロフィールガードを今後表示しない設定
  function isOptOut() {
    return localStorage.getItem(OPT_KEY) === '1';
  }

  // 一時スヌーズ中かどうか
  function isSnoozedByLocal() {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || '0');
    return Date.now() < until;
  }
};

export default MypageRecords;
